import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import html2canvas from "html2canvas";

export const handleExportExcel = async ({
  analyticsData,
  startDate,
  endDate,
  t,
  formatDuration,
  folders,
  schedule,
  device,
  getDeviceOffsetMin,
  classIdToMeta,
  setExportingExcel,
  toast,
  volumeMode
}) => {
  if (!analyticsData) {
    toast.warn("Сначала загрузите аналитику");
    return;
  }
  setExportingExcel(true);

  // === ОПРЕДЕЛЕНИЕ ТЕКУЩЕГО РЕЖИМА РАСЧЕТА ===
  const isSolid = volumeMode === "solid";
  const isTwin = volumeMode === "twin";
  const isTwinSolid = volumeMode === "twin_solid";

  const aiVolRatio = (analyticsData.estimated_volume_total > 0 && analyticsData.ai_volume_total > 0)
      ? (analyticsData.ai_volume_total / analyticsData.estimated_volume_total) : 1.0;
      
  const aiMassRatio = (analyticsData.estimated_mass_total > 0 && analyticsData.ai_mass_total > 0)
      ? (analyticsData.ai_mass_total / analyticsData.estimated_mass_total) : 1.0;

  const globalSolidRatio = (analyticsData.estimated_volume_total > 0 && analyticsData.solid_volume_total > 0)
      ? (analyticsData.solid_volume_total / analyticsData.estimated_volume_total) : 1.0;

  const volLabel = isTwinSolid ? "AI Объем в твердом теле"
                 : isTwin ? "AI Объем цифрового двойника" 
                 : isSolid ? "Объем в твердом теле" 
                 : "Номинальный объем";

  const massLabel = (isTwin || isTwinSolid) ? "AI Масса цифрового двойника" : "Номинальная масса";

  // Вспомогательная функция для получения правильного объема
  const getVolValue = (type) => {
    const est = analyticsData[`estimated_volume_${type}`] || 0;
    const sol = analyticsData[`solid_volume_${type}`] || 0;
    const ai = analyticsData[`ai_volume_${type}`] ?? (est * aiVolRatio);
    
    if (isTwinSolid) {
        const sr = est > 0 ? sol / est : globalSolidRatio;
        return `${(ai * sr).toFixed(1)} м³`;
    }
    if (isTwin) return `${ai.toFixed(1)} м³`;
    if (isSolid) return `${sol.toFixed(1)} м³`;
    return `${est.toFixed(1)} м³`;
  };

  // Вспомогательная функция для получения правильной массы
  const getMassValue = (type) => {
    const est = analyticsData[`estimated_mass_${type}`] || 0;
    const ai = analyticsData[`ai_mass_${type}`] ?? (est * aiMassRatio);
    const mass = (isTwin || isTwinSolid) ? ai : est;
    return `${(mass / 1000).toFixed(1)} т`;
  };

  // Функция для раскраски KPI процентов (0-40, 40-60, 60-100)
  const getKpiFill = (val) => {
    if (typeof val !== 'number') return null;
    if (val <= 40) return { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF66" } }; // Желтый
    if (val <= 60) return { type: "pattern", pattern: "solid", fgColor: { argb: "FF99FF99" } }; // Зеленый
    return { type: "pattern", pattern: "solid", fgColor: { argb: "FFFF8080" } }; // Красный
  };

  try {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "System AI";
    workbook.created = new Date();
    const sheetSummary = workbook.addWorksheet("Сводная статистика", {
      views: [{ showGridLines: false }],
    });

    sheetSummary.columns = [
      { key: "param", width: 45 },
      { key: "val", width: 25 },
    ];

    const titleRow = sheetSummary.addRow({ param: "=== ИНФОРМАЦИЯ ОБ ОТЧЕТЕ ===" });
    titleRow.font = { bold: true, size: 12 };
    sheetSummary.addRow({ param: "Период:", val: `${dayjs(startDate).format("DD.MM.YYYY HH:mm")} - ${dayjs(endDate).format("DD.MM.YYYY HH:mm")}` });
    sheetSummary.addRow({ param: "Участок:", val: device?.work_zone_name || "Без привязки к участку" });
    sheetSummary.addRow({ param: "Техника:", val: device?.name || "" });
    sheetSummary.addRow({});
    
    const headerRow = sheetSummary.addRow({ param: "Параметр", val: "Значение" });
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4472C4" },
    };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.height = 30;

    const usefulMh = analyticsData.kpis?.useful_work_mh || 0;
    const scheduledSec = analyticsData.total_duration_sec || 0;
    const noDataSec = analyticsData.durations_by_class_sec?.no_data || 0;
    const uptimeMh = Math.max(0, scheduledSec - noDataSec) / 3600.0;
    const usefulPct = uptimeMh > 0 ? (usefulMh / uptimeMh) * 100 : 0;

    // Расчет чистой часовой производительности (м³/час без простоев)
    const activeVolTotal = isTwinSolid
      ? (analyticsData.ai_volume_total || 0) * globalSolidRatio
      : isTwin
        ? (analyticsData.ai_volume_total || 0)
        : isSolid
          ? (analyticsData.solid_volume_total || 0)
          : (analyticsData.estimated_volume_total || 0);
    const m3PerHour = usefulMh > 0 ? activeVolTotal / usefulMh : 0;

    const summaryData = [
      {
        param: "OEE %",
        val: `${(analyticsData.kpis?.OEE || 0).toFixed(1)}%`,
        isKpi: true,
        numVal: analyticsData.kpis?.OEE || 0
      },
      {
        param: "КИО %",
        val: `${(analyticsData.kpis?.KIO || 0).toFixed(1)}%`,
        isKpi: true,
        numVal: analyticsData.kpis?.KIO || 0
      },
      {
        param: "КТГ %",
        val: `${(analyticsData.kpis?.KTG || 0).toFixed(1)}%`,
        isKpi: true,
        numVal: analyticsData.kpis?.KTG || 0
      },
      {
        param: "КИВ %",
        val: `${(analyticsData.kpis?.KIV || 0).toFixed(1)}%`,
        isKpi: true,
        numVal: analyticsData.kpis?.KIV || 0
      },
      {
        param: "КИН %",
        val: `${(analyticsData.kpis?.KIN || 0).toFixed(1)}%`,
        isKpi: true,
        numVal: analyticsData.kpis?.KIN || 0
      },
      
      {
        param: t("analysis.useful_time", "Полезное время"),
        val: `${usefulMh.toFixed(1)} / ${uptimeMh.toFixed(1)} маш.-ч (${usefulPct.toFixed(1)}%)`,
      },
      {
        param: "Производительность в чистый час работы (м³/ч)",
        val: `${m3PerHour.toFixed(1)} м³/ч`,
      },
      {
        param: "Пробег по GPS (км)",
        val: analyticsData.total_mileage_km?.toFixed(2),
      },
      { param: "", val: "" },
      { param: "Всего циклов", val: analyticsData.cycle_count_total },
      { param: "Циклов в машину", val: analyticsData.cycle_count_vehicle },
      { param: "Циклов в землю", val: analyticsData.cycle_count_ground },
      { param: "", val: "" },
      
      // === ДИНАМИЧЕСКИЕ ЗНАЧЕНИЯ ОБЪЕМА И МАССЫ (В ЗАВИСИМОСТИ ОТ РЕЖИМА) ===
      { param: `${volLabel} (всего)`, val: getVolValue('total') },
      { param: `${volLabel} (в машину)`, val: getVolValue('vehicle') },
      { param: `${volLabel} (в землю)`, val: getVolValue('ground') },
      { param: `${massLabel} (всего)`, val: getMassValue('total') },
      { param: `${massLabel} (в машину)`, val: getMassValue('vehicle') },
      { param: `${massLabel} (в землю)`, val: getMassValue('ground') },
      { param: "", val: "" },
      
      { param: "=== ВИРТУАЛЬНЫЙ РАСХОД ТОПЛИВА ===", val: "", isHeader: true },
      {
        param: "Итоговый расчетный расход",
        val: analyticsData.virtual_fuel ? `${analyticsData.virtual_fuel.total_liters} л` : "Спецификация не задана",
      },
      {
        param: "  - На холостом ходу",
        val: analyticsData.virtual_fuel ? `${analyticsData.virtual_fuel.liters_idle} л` : "-",
      },
      {
        param: "  - При средней нагрузке",
        val: analyticsData.virtual_fuel ? `${analyticsData.virtual_fuel.liters_medium} л` : "-",
      },
      {
        param: "  - При высокой нагрузке",
        val: analyticsData.virtual_fuel ? `${analyticsData.virtual_fuel.liters_high} л` : "-",
      },
      { param: "", val: "" },
      {
        param: "Простой (двигатель включен)",
        val: formatDuration(
          analyticsData.durations_by_class_sec.actual_downtime,
        ),
      },
      {
        param: "Простой (двигатель выключен)",
        val: formatDuration(
          analyticsData.durations_by_class_sec.no_data,
        ),
      },
      {
        param: "Нерабочее время",
        val: formatDuration(
          analyticsData.durations_by_class_sec.non_working_time,
        ),
      },
    ];

    if (analyticsData.illegal_work && analyticsData.illegal_work.cycles > 0) {
      summaryData.push(
        { param: "", val: "" },
        { param: "=== РАБОТА ВНЕ СМЕНЫ ===", val: "", isHeader: true },
        { param: "  Количество циклов (ковшей)", val: analyticsData.illegal_work.cycles },
        { param: "  Объем левой выработки (м³)", val: `${analyticsData.illegal_work.volume.toFixed(1)} м³` },
        { param: "  Масса левой выработки (т)", val: `${analyticsData.illegal_work.mass_tons.toFixed(1)} т` },
        { param: "  Чистое время работы вне смены", val: formatDuration(analyticsData.illegal_work.duration_sec) },
      );
    }
    
    // Показываем дополнительный блок Двойника только если выбран ДРУГОЙ режим,
    // но в настройках машины двойник включен (как справочная информация)
    if (analyticsData.use_digital_twin && !isTwin) {
      summaryData.push(
        { param: "", val: "" },
        {
          param: "AI Объем двойника (всего)",
          val: `${analyticsData.ai_volume_total?.toFixed(1)} м³`,
        },
        {
          param: "AI Объем двойника (в машину)",
          val: `${analyticsData.ai_volume_vehicle?.toFixed(1)} м³`,
        },
        {
          param: "AI Объем двойника (в землю)",
          val: `${analyticsData.ai_volume_ground?.toFixed(1)} м³`,
        },
        {
          param: "AI Масса двойника (всего)",
          val: `${(analyticsData.ai_mass_total / 1000).toFixed(1)} т`,
        },
      );
    }

    summaryData.push(
      { param: "", val: "" },
      {
        param: t("analysis.truck_arrivals_total", "Общее кол-во подъездов"),
        val: analyticsData.truck_arrivals_total || 0,
      },
      {
        param: t("analysis.avg_loading_session", "Ср. время загрузки машины"),
        val: formatDuration(analyticsData.averages.vehicle.loading_session_sec),
      },
      {
        param: "Средний цикл (в машину)",
        val: `${analyticsData.averages.vehicle.total_sec} сек`,
      },
      {
        param: "  - Возврат и копание",
        val: `${analyticsData.averages.vehicle.empty_sec} сек`,
      },
      {
        param: "  - Поворот на разгрузку",
        val: `${analyticsData.averages.vehicle.full_sec} сек`,
      },
      {
        param: "  - Процесс разгрузки",
        val: `${analyticsData.averages.vehicle.unload_sec} сек`,
      },
      {
        param: "  - Орг. задержки (микро-простои)",
        val: `${analyticsData.averages.vehicle.delay_sec || 0} сек`,
      },
      { param: "", val: "" },
      {
        param: "Средний цикл (в землю)",
        val: `${analyticsData.averages.ground.total_sec} сек`,
      },
      {
        param: "  - Возврат и копание",
        val: `${analyticsData.averages.ground.empty_sec} сек`,
      },
      {
        param: "  - Поворот на разгрузку",
        val: `${analyticsData.averages.ground.full_sec} сек`,
      },
      {
        param: "  - Процесс разгрузки",
        val: `${analyticsData.averages.ground.unload_sec} сек`,
      },
      { param: "", val: "" },
      {
        param: "Время с полным ковшом",
        val: formatDuration(analyticsData.durations_by_class_sec.full_bucket),
      },
      {
        param: "Время разгрузки (в машину)",
        val: formatDuration(
          analyticsData.durations_by_class_sec.unloading_vehicle,
        ),
      },
      {
        param: "Время загрузки ковша",
        val: formatDuration(
          analyticsData.durations_by_class_sec.unloading_ground,
        ),
      },
      {
        param: "Время с пустым ковшом",
        val: formatDuration(analyticsData.durations_by_class_sec.empty_bucket),
      },
      {
        param: "Время простоя техники",
        val: formatDuration(
          analyticsData.durations_by_class_sec.actual_downtime,
        ),
      },
      {
        param: t("analysis.time_downtime", "Время (нет данных)"),
        val: formatDuration(
          analyticsData.durations_by_class_sec.no_data,
        ),
      },
    );

    if (analyticsData.shift_stats) {
      summaryData.push(
        { param: "", val: "" },
        { param: "=== ДЕТАЛИЗАЦИЯ ПО СМЕНАМ ===", val: "", isHeader: true },
      );
      Object.entries(analyticsData.shift_stats).forEach(([name, stats]) => {
        // Подсчет объемов для сводки Excel
        const calcVol = (nom, sol, aiVal) => {
           let ai = aiVal ?? (nom * aiVolRatio);
           if (isTwinSolid) return ai * (nom > 0 ? sol/nom : globalSolidRatio);
           if (isTwin) return ai;
           if (isSolid) return sol;
           return nom;
        };
        const calcMass = (nom, aiVal) => (isTwin || isTwinSolid) ? (aiVal ?? (nom * aiMassRatio)) : nom;

        summaryData.push(
          { param: `Смена: ${name}`, val: "", isSubHeader: true },
          {
            param: "  Циклы (всего / авто / земля)",
            val: `${stats.cycles_total} / ${stats.cycles_vehicle} / ${stats.cycles_ground}`,
          },
          {
            param: "  Объем (всего / авто / земля)",
            val: `${Number(calcVol(stats.volume_total, stats.solid_volume_total, stats.ai_volume_total)).toFixed(1)} / ${Number(calcVol(stats.volume_vehicle, stats.solid_volume_vehicle, stats.ai_volume_vehicle)).toFixed(1)} / ${Number(calcVol(stats.volume_ground, stats.solid_volume_ground, stats.ai_volume_ground)).toFixed(1)} м³`,
          },
          {
            param: "  Масса (всего / авто / земля)",
            val: `${(Number(calcMass(stats.mass_total, stats.ai_mass_total)) / 1000).toFixed(1)} / ${(Number(calcMass(stats.mass_vehicle, stats.ai_mass_vehicle)) / 1000).toFixed(1)} / ${(Number(calcMass(stats.mass_ground, stats.ai_mass_ground)) / 1000).toFixed(1)} т`,
          },
          {
            param: "  Простой / Нет данных",
            val: formatDuration(stats.actual_downtime + stats.no_data),
          },
        );
      });
    }

    if (
      analyticsData.truck_stats_summary &&
      Object.keys(analyticsData.truck_stats_summary).length > 0
    ) {
      summaryData.push(
        { param: "", val: "" },
        { param: "=== СТАТИСТИКА ПО САМОСВАЛАМ ===", val: "", isHeader: true },
      );
      Object.entries(analyticsData.truck_stats_summary).forEach(
        ([label, stats]) => {
          let s_vol = stats.vol || 0;
          let sol_vol = stats.solid_vol || 0;
          let ai_vol = stats.ai_vol ?? (s_vol * aiVolRatio);
          let final_vol = isTwinSolid ? (ai_vol * (s_vol > 0 ? sol_vol/s_vol : globalSolidRatio)) : isTwin ? ai_vol : isSolid ? sol_vol : s_vol;

          let s_mass = stats.mass || 0;
          let ai_mass = stats.ai_mass ?? (s_mass * aiMassRatio);
          let final_mass = (isTwin || isTwinSolid) ? ai_mass : s_mass;

          summaryData.push(
            { param: `Самосвал: ${label}`, val: "", isSubHeader: true },
            { param: "  Подъездов", val: stats.arrivals },
            { param: "  Всего ковшей", val: stats.cycles },
            {
              param: "  Ковшей (Мин / Макс / Ср)",
              val: `${stats.min_cycles} / ${stats.max_cycles} / ${stats.avg_cycles}`,
            },
            {
              param: "  Объем породы",
              val: `${Number(final_vol).toFixed(1)} м³`,
            },
            {
              param: "  Масса породы",
              val: `${(Number(final_mass) / 1000).toFixed(1)} т`,
            },
          );
        },
      );
    }

    summaryData.forEach((item) => {
      const row = sheetSummary.addRow({ param: item.param, val: item.val });
      if (item.param === "") {
        row.height = 10;
      } else {
        row.height = 20;
        row.getCell(1).alignment = { vertical: "middle", horizontal: "left" };
        row.getCell(2).alignment = { vertical: "middle", horizontal: "center" };
        
        if (item.isHeader) {
          row.getCell(1).font = {
            bold: true,
            size: 11,
            color: { argb: "FFFFFFFF" },
          };
          row.getCell(1).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF5A5A5A" },
          };
        } else if (item.isSubHeader) {
          row.getCell(1).font = { bold: true, color: { argb: "FF000000" } };
          row.getCell(1).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFEFEFEF" },
          };
        } else {
          row.getCell(1).border = {
            bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
          };
          row.getCell(2).border = {
            bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
          };
          
          if (item.isKpi && item.numVal != null) {
            const fill = getKpiFill(item.numVal);
            if (fill) row.getCell(2).fill = fill;
          }
        }
      }
      if (item.param.includes("в машину") && typeof item.val === "number") {
        row.getCell(2).font = { color: { argb: "FF0000FF" }, bold: true };
      }
    });

    const analyticsElement = document.getElementById("analytics-content");
    if (analyticsElement) {
      try {
        const canvas = await html2canvas(analyticsElement, { scale: 1.5 });
        const imgData = canvas.toDataURL("image/png");
        const imageId = workbook.addImage({
          base64: imgData,
          extension: "png",
        });
        sheetSummary.addImage(imageId, {
          tl: { col: 3, row: 1 },
          ext: { width: canvas.width / 1.5, height: canvas.height / 1.5 },
        });
      } catch (e) {
        console.error(e);
      }
    }

    const sheetArchive = workbook.addWorksheet("Видеоархив");
    sheetArchive.columns = [
      { header: "ID", key: "id", width: 10 },
      { header: "Дата", key: "date", width: 15 },
      { header: "Время начала", key: "start", width: 15 },
      { header: "Время окончания", key: "end", width: 18 },
      { header: "Кол-во файлов", key: "files", width: 15 },
      { header: "Статус", key: "status", width: 15 },
    ];

    const archHeaderRow = sheetArchive.getRow(1);
    archHeaderRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    archHeaderRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF202020" },
    };
    archHeaderRow.alignment = { vertical: "middle", horizontal: "center" };
    archHeaderRow.height = 25;

    folders.forEach((f, index) => {
      const end = dayjs(f.process_after).utcOffset(
        getDeviceOffsetMin(schedule.timezone_offset),
      );
      const start = end.subtract(1, "hour");
      const row = sheetArchive.addRow({
        id: f.id,
        date: start.format("DD.MM.YYYY"),
        start: start.format("HH:00"),
        end: end.format("HH:00"),
        files: f.file_count,
        status: f.video_file ? "Готово" : "Обработка",
      });
      if (index % 2 !== 0) {
        row.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF5F5F5" },
          };
        });
      }
      row.alignment = { vertical: "middle", horizontal: "center" };
      const statusCell = row.getCell(6);
      if (f.video_file) {
        statusCell.font = { color: { argb: "FF009900" } };
      } else {
        statusCell.font = { color: { argb: "FF999999" } };
      }
    });

    // =========================================================================
    // ДИНАМИКА РАБОТЫ (ПО ДНЯМ / ЧАСАМ)
    // =========================================================================
    const chartData = analyticsData.productivity_chart?.data;
    if (chartData && chartData.length > 0) {
      const isHourly = analyticsData.productivity_chart.is_hourly;
      const sheetDyn = workbook.addWorksheet(isHourly ? "Динамика (по часам)" : "Динамика (по дням)", { views: [{ showGridLines: false }] });
      const dynCols = [
        { header: "Параметр", key: "param", width: 35 },
        { header: "ИТОГО (SUM)", key: "sum", width: 15 },
        { header: "СРЕДНЕЕ (AVG)", key: "avg", width: 15 }
      ];

      chartData.forEach((d, i) => {
        let headerLabel = d.time;

        if (isHourly) {
          const parts = d.time.split(' ');
          if (parts.length >= 2) {
            const datePart = parts[0];
            const timePart = parts[1];
            const hour = parseInt(timePart.split(':')[0], 10);
            
            if (!isNaN(hour)) {
              const nextHourStr = ((hour + 1) % 24).toString().padStart(2, '0') + ":00";
              headerLabel = `${datePart} ${timePart} - ${nextHourStr}`;
            }
          }
        }
        
        dynCols.push({ header: headerLabel, key: `col_${i}`, width: isHourly ? 22 : 16 });
      });
      
      sheetDyn.columns = dynCols;

      const dynHeader = sheetDyn.getRow(1);
      dynHeader.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
      dynHeader.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
      dynHeader.alignment = { horizontal: "center", vertical: "middle" };
      dynHeader.height = 30;

      const addDynRow = (title, dataKey, isSolidKey = null, isTime = false) => {
        const targetKey = (isSolid || isTwinSolid) && isSolidKey ? isSolidKey : dataKey;
        
        const values = chartData.map(d => {
            let v = Number(d[dataKey]) || 0;
            let sol = Number(d[isSolidKey]) || 0;
            let sr = v > 0 ? sol / v : globalSolidRatio;
            
            if (isTwinSolid && isSolidKey) return (v * aiVolRatio) * sr;
            if (isTwin && isSolidKey) return v * aiVolRatio;
            if (isSolid && isSolidKey) return sol;
            return Number(d[targetKey]) || 0;
        });

        const sum = values.reduce((a, b) => a + b, 0);
        const avg = values.length ? sum / values.length : 0;

        const rowDict = {
            param: title,
            sum: isTime ? formatDuration(sum) : Number(sum.toFixed(1)),
            avg: isTime ? formatDuration(avg) : Number(avg.toFixed(1))
        };

        values.forEach((v, i) => {
            rowDict[`col_${i}`] = isTime ? formatDuration(v) : Number(v.toFixed(1));
        });

        const row = sheetDyn.addRow(rowDict);
        row.height = 20;
        
        row.getCell(1).font = { bold: true };
        row.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
        row.getCell(2).font = { bold: true, color: { argb: "FF0070C0" } }; 
        row.getCell(3).font = { bold: true, color: { argb: "FF009900" } }; 

        row.eachCell((cell, colNum) => {
          cell.border = { bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } } };
          if (colNum > 1) cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
      };

      addDynRow(volLabel + " (всего)", "volume_total", isSolid ? "solid_volume_total" : null);
      addDynRow(volLabel + " (в машину)", "volume_vehicle", isSolid ? "solid_volume_vehicle" : null);
      addDynRow(volLabel + " (в отвал)", "volume_ground", isSolid ? "solid_volume_ground" : null);

      // --- ДОБАВЛЯЕМ НАКОПИТЕЛЬНЫЙ ИТОГ (S-Кривая в цифрах) ---
      let runSum = 0;
      const cumValues = chartData.map(d => {
         let v = Number(d["volume_total"]) || 0;
         let sol = Number(d["solid_volume_total"]) || 0;
         let sr = v > 0 ? sol/v : globalSolidRatio;
         
         let stepVol = v;
         if (isTwinSolid) stepVol = (v * aiVolRatio) * sr;
         else if (isTwin) stepVol = v * aiVolRatio;
         else if (isSolid) stepVol = sol;
         
         runSum += stepVol;
         return runSum;
      });
      
      const cumRowDict = { param: "📈 НАКОПИТЕЛЬНЫЙ ОБЪЕМ (м³)", sum: "-", avg: "-" };
      cumValues.forEach((v, i) => { cumRowDict[`col_${i}`] = Number(v.toFixed(1)); });
      const cumRow = sheetDyn.addRow(cumRowDict);
      cumRow.height = 25;
      
      cumRow.eachCell((cell, colNum) => {
          cell.font = { bold: true, color: { argb: "FFD2691E" } }; 
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF5EE" } };
          cell.border = { bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } }, top: { style: 'thin', color: { argb: 'FFCCCCCC' } } };
          cell.alignment = { horizontal: colNum === 1 ? 'left' : 'center', vertical: 'middle' };
      });

      sheetDyn.addRow([]); // Пустая строка-отступ

      addDynRow("Циклы (в машину)", "cycles_vehicle");
      addDynRow("Циклы (в отвал)", "cycles_ground");
      addDynRow("Количество подъездов самосвалов", "trucks");

      sheetDyn.addRow([]); // Пустая строка-отступ

      addDynRow("Простой техники (ч:м:с)", "downtime_sec", null, true);
    }
    // =========================================================================

    // =========================================================================
    // ЖУРНАЛ СОСТОЯНИЙ
    // =========================================================================
    const sheetTimeline = workbook.addWorksheet("Журнал состояний");
    const events = [];

    const toExcelDate = (isoString) => {
      if (!isoString) return null;
      const d = dayjs(isoString).utcOffset(getDeviceOffsetMin(schedule.timezone_offset));
      return new Date(Date.UTC(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second()));
    };

    if (analyticsData.timeline) {
      analyticsData.timeline.forEach(item => {
        const start = dayjs(item.start);
        const end = dayjs(item.end);
        const durSec = end.diff(start, 'second');
        const meta = classIdToMeta[item.class_id] || { label: "Неизвестно" };

        events.push({
          startIso: item.start,
          endIso: item.end,
          durSec: durSec,
          event: meta.label,
          type: "Состояние экскаватора",
          shift: item.shift_name || "-"
        });
      });
    }

    if (analyticsData.truck_sessions && analyticsData.date_range) {
      const rangeStart = dayjs(analyticsData.date_range.start);
      const rangeEnd = dayjs(analyticsData.date_range.end);
      let currentTimeMs = rangeStart.valueOf();

      const sortedSessions = [...analyticsData.truck_sessions].sort((a, b) => new Date(a.start) - new Date(b.start));
      const waitLabel = t("classes.idle_waiting", "Ожидание самосвала");

      sortedSessions.forEach(session => {
        let sMs = dayjs(session.start).valueOf();
        let eMs = dayjs(session.end).valueOf();

        if (eMs === sMs) eMs += 30000; 
        if (sMs < currentTimeMs) sMs = currentTimeMs;
        if (eMs <= sMs) eMs = sMs + 1000;

        if (sMs > currentTimeMs) {
          const durSec = (sMs - currentTimeMs) / 1000;
          events.push({
            startIso: new Date(currentTimeMs).toISOString(),
            endIso: new Date(sMs).toISOString(),
            durSec: durSec,
            event: waitLabel,
            type: "Логистика",
            shift: "-"
          });
        }

        const durSec = (eMs - sMs) / 1000;
        events.push({
          startIso: new Date(sMs).toISOString(),
          endIso: new Date(eMs).toISOString(),
          durSec: durSec,
          event: `Погрузка: ${session.label} (${session.cycles} ковш.)`,
          type: "Логистика",
          shift: "-"
        });

        currentTimeMs = eMs;
      });

      const rEndMs = rangeEnd.valueOf();
      if (currentTimeMs < rEndMs) {
        const durSec = (rEndMs - currentTimeMs) / 1000;
        events.push({
          startIso: new Date(currentTimeMs).toISOString(),
          endIso: new Date(rEndMs).toISOString(),
          durSec: durSec,
          event: waitLabel,
          type: "Логистика",
          shift: "-"
        });
      }
    }

    events.sort((a, b) => new Date(a.startIso) - new Date(b.startIso));

    const summaryByEvent = {};
    events.forEach(e => {
      summaryByEvent[e.event] = (summaryByEvent[e.event] || 0) + e.durSec;
    });

    sheetTimeline.columns = [
      { key: "start", width: 22 },
      { key: "end", width: 22 },
      { key: "duration", width: 15 },
      { key: "event", width: 45 },
      { key: "type", width: 22 },
      { key: "shift", width: 25 },
    ];

    const tlTitle = sheetTimeline.addRow({ start: "СУММАРНОЕ ВРЕМЯ ПО СТАТУСАМ" });
    tlTitle.font = { bold: true, size: 12 };
    
    const sortedSummary = Object.entries(summaryByEvent).sort((a, b) => b[1] - a[1]);
    sortedSummary.forEach(([eventName, totalSec]) => {
      const row = sheetTimeline.addRow({
        start: eventName,
        end: formatDuration(totalSec)
      });
      row.getCell(1).font = { bold: true };
      row.getCell(2).font = { bold: true, color: { argb: "FF0070C0" } };
    });

    sheetTimeline.addRow({});
    const logTitle = sheetTimeline.addRow({ start: "ДЕТАЛЬНЫЙ ЖУРНАЛ" });
    logTitle.font = { bold: true, size: 12 };

    const tlHeader = sheetTimeline.addRow({
      start: "Время начала", end: "Время окончания", duration: "Длительность", event: "Событие / Статус", type: "Категория", shift: "Смена"
    });
    tlHeader.font = { bold: true, color: { argb: "FFFFFFFF" } };
    tlHeader.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };

    events.forEach(e => {
      const row = sheetTimeline.addRow({
        start: toExcelDate(e.startIso),
        end: toExcelDate(e.endIso),
        duration: formatDuration(e.durSec),
        event: e.event,
        type: e.type,
        shift: e.shift
      });
      row.getCell(1).numFmt = 'dd.mm.yyyy hh:mm:ss';
      row.getCell(2).numFmt = 'dd.mm.yyyy hh:mm:ss';
      
      if (e.event.includes("Ожидание")) row.getCell(4).font = { color: { argb: "FFC00000" } };
      if (e.event.includes("Погрузка")) row.getCell(4).font = { color: { argb: "FF00B050" } };
    });
    // =========================================================================

    const buffer = await workbook.xlsx.writeBuffer();
    const filename = `${device?.name || "Device"}_Analytics_${dayjs().format("YYYY-MM-DD")}.xlsx`;
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, filename);
    toast.success(t("common.success"));
  } catch (error) {
    console.error(error);
    toast.error(t("common.error"));
  } finally {
    setExportingExcel(false);
  }
};